import { useContext, useEffect } from 'react';
import { AngularContext } from 'react-app';
import t from 'react-translate';

import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getMentoringProgram } from 'redux/actions/mentoring-programs';
import { MentorRequestSource } from 'redux/schemas/models/mentor-request';
import { MentorshipProgramRequest } from 'redux/schemas/models/mentorship-program-connections';
import {
  getMentoringProgramEnrollment as getEnrollmentFromRedux,
} from 'redux/selectors/mentoring-program-enrollments';
import { getMentoringProgramEnrollment } from 'redux/actions/mentoring-program-enrollments';
import { MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgram as getProgram } from 'redux/selectors/mentoring-programs';

import { css } from '@emotion/react';
import { gray3 } from 'styles/global_defaults/colors';

import ProfilePicture from 'org_level_profile/components/profile-picture';
import ClickableContainer from 'components/clickable-container';

const styles = css`
  a {
    .no-link {
      text-decoration: none;
      color: ${gray3} !important;
    }
  }
`;

type MentoringRequest = MentorshipProgramRequest & {
  programId: number;
}

const MentoringRequestTemplate = () => {
  const { $scope, injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const dispatch = useAppDispatch();
  const request: MentoringRequest = $scope.$parent.mentoringRequest;
  const {
    id: mentorRequest,
    menteeEnrollmentId,
    mentorEnrollmentId,
    programId,
    message,
  } = request;

  const MENTORING_REQUEST_LINK_ID = `mentoring-request-link-${mentorRequest}`;

  useEffect(() => {
    const linkElement = document.getElementById(MENTORING_REQUEST_LINK_ID);

    if (linkElement) {
      const onClick = (e: MouseEvent) => {
        e.preventDefault();
        $state.go('mentoring-program-single-connection', {
          programId,
          mentorRequest,
          source: MentorRequestSource.MENTOR_HOME,
          showLimitModal: false,
        });
      };
      linkElement.addEventListener('click', onClick);

      return () => {
        linkElement.removeEventListener('click', onClick);
      };
    }
  }, []);

  const mentoringProgram = useSelector((state) => getProgram(state, programId));
  const { programAlias } = mentoringProgram || {};
  const mentorAlias = programAlias?.mentor?.downcasedSingularized || '';

  const menteeEnrollment: MentoringProgramEnrollment = useSelector((state) => (
    getEnrollmentFromRedux(state, menteeEnrollmentId)
  ));
  const mentorEnrollment: MentoringProgramEnrollment = useSelector((state) => (
    getEnrollmentFromRedux(state, mentorEnrollmentId)
  ));

  const fullName = menteeEnrollment?.user?.fullName || '';

  useEffect(() => {
    if (!menteeEnrollment) {
      dispatch(getMentoringProgramEnrollment({
        programId,
        enrollmentId: menteeEnrollmentId,
      }));
    }
  }, [request.menteeEnrollmentId]);

  useEffect(() => {
    if (!mentorEnrollment) {
      dispatch(getMentoringProgramEnrollment({
        programId,
        enrollmentId: mentorEnrollmentId,
      }));
    }
  }, [request.mentorEnrollmentId]);

  useEffect(() => {
    if (!mentoringProgram) {
      dispatch(getMentoringProgram(programId));
    }
  }, [programId]);

  return (
    <div css={styles} className='request-and-invitation'>
      <div className='left-column'>
        {menteeEnrollment?.user && (
          <ProfilePicture profile={menteeEnrollment?.user || {}} size={30} />
        )}
      </div>
      <ClickableContainer
        className='right-column d-flex flex-column'
      >
        <a id={MENTORING_REQUEST_LINK_ID} href='#'>
          <p>
            {t.MENTORING_PROGRAMS.PARTICIPANT_HOME.MENTOR_REQUEST_MODAL.MENTOR_VIEW.MENTOR_REQUEST_NOTIFICATION({
            fullName,
            mentorAlias,
          })}
          </p>
          <div dangerouslySetInnerHTML={{ __html: message }} className='no-link' />
        </a>
      </ClickableContainer>
    </div>
  );
};

export default MentoringRequestTemplate;
